import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendChart, EnhancedBarChart, EnhancedPieChart } from '../components/charts/AdvancedCharts';
import { financialService } from '../services/financial';
import { formatCurrency } from '../utils/formatters';
import { TrendingUp, TrendingDown, DollarSign, PieChart as PieChartIcon, BarChart3 } from 'lucide-react';
import {
  StyledAnalyticsContainer,
  StyledHeader,
  StyledTitle,
  StyledPeriodSelector,
  StyledStatsGrid,
  StyledStatCard,
  StyledStatIcon,
  StyledStatInfo,
  StyledStatLabel,
  StyledStatValue,
  StyledChartsGrid,
  StyledChartCard,
  StyledChartTitle,
  StyledInsightsList,
  StyledInsightCard,
  StyledInsightText,
  StyledLoadingContainer,
  StyledLoadingSpinner,
  StyledLoadingText,
  StyledErrorContainer,
  StyledErrorText,
} from './AnalyticsPage.styles';

interface AnalyticsData {
  monthlyTrends: Array<{ month: string; income: number; expenses: number }>;
  categoryBreakdown: Array<{ category: string; amount: number; percentage: number }>;
  insights: string[];
}

const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('6months');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const response = await financialService.getAnalytics(period);

        setData(response.data || null);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
        // Fallback to mock data
        setData({
          monthlyTrends: [
            { month: 'Jan', income: 5000, expenses: 3500 },
            { month: 'Feb', income: 5200, expenses: 3200 },
            { month: 'Mar', income: 4800, expenses: 3800 },
            { month: 'Apr', income: 5500, expenses: 4000 },
            { month: 'May', income: 5300, expenses: 3600 },
            { month: 'Jun', income: 5600, expenses: 3900 },
          ],
          categoryBreakdown: [
            { category: 'Food', amount: 1200, percentage: 25 },
            { category: 'Transportation', amount: 800, percentage: 17 },
            { category: 'Entertainment', amount: 600, percentage: 12 },
            { category: 'Utilities', amount: 500, percentage: 10 },
            { category: 'Shopping', amount: 900, percentage: 19 },
            { category: 'Other', amount: 800, percentage: 17 },
          ],
          insights: [
            'Your spending on food has increased by 15% compared to last month.',
            'Transportation costs are 20% lower than average - great job!',
            'Consider setting a budget limit for entertainment expenses.',
            'Your savings rate has improved by 5% this quarter.',
          ],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [period]);

  if (loading) {
    return (
      <StyledAnalyticsContainer>
        <StyledLoadingContainer>
          <StyledLoadingSpinner />
          <StyledLoadingText>{t('common.loading')}</StyledLoadingText>
        </StyledLoadingContainer>
      </StyledAnalyticsContainer>
    );
  }

  if (!data) {
    return (
      <StyledAnalyticsContainer>
        <StyledErrorContainer>
          <StyledErrorText>{t('common.error')}</StyledErrorText>
        </StyledErrorContainer>
      </StyledAnalyticsContainer>
    );
  }

  const totalIncome = data.monthlyTrends.reduce((sum, item) => sum + item.income, 0);
  const totalExpenses = data.monthlyTrends.reduce((sum, item) => sum + item.expenses, 0);
  const netIncome = totalIncome - totalExpenses;
  const savingsRate = totalIncome > 0 ? (netIncome / totalIncome) * 100 : 0;

  return (
    <StyledAnalyticsContainer>
      <StyledHeader>
        <StyledTitle>
          <BarChart3 size={32} />
          {t('analytics.title')}
        </StyledTitle>
        <StyledPeriodSelector value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value='3months'>{t('analytics.periods.3months')}</option>
          <option value='6months'>{t('analytics.periods.6months')}</option>
          <option value='12months'>{t('analytics.periods.12months')}</option>
        </StyledPeriodSelector>
      </StyledHeader>
      <StyledStatsGrid>
        <StyledStatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StyledStatIcon $color='#10b981'>
            <TrendingUp size={24} />
          </StyledStatIcon>
          <StyledStatInfo>
            <StyledStatLabel>{t('analytics.stats.totalIncome')}</StyledStatLabel>
            <StyledStatValue>{formatCurrency(totalIncome)}</StyledStatValue>
          </StyledStatInfo>
        </StyledStatCard>

        <StyledStatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StyledStatIcon $color='#ef4444'>
            <TrendingDown size={24} />
          </StyledStatIcon>
          <StyledStatInfo>
            <StyledStatLabel>{t('analytics.stats.totalExpenses')}</StyledStatLabel>
            <StyledStatValue>{formatCurrency(totalExpenses)}</StyledStatValue>
          </StyledStatInfo>
        </StyledStatCard>

        <StyledStatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StyledStatIcon $color='#6366f1'>
            <DollarSign size={24} />
          </StyledStatIcon>
          <StyledStatInfo>
            <StyledStatLabel>{t('analytics.stats.netIncome')}</StyledStatLabel>
            <StyledStatValue>{formatCurrency(netIncome)}</StyledStatValue>
          </StyledStatInfo>
        </StyledStatCard>

        <StyledStatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <StyledStatIcon $color='#f59e0b'>
            <PieChartIcon size={24} />
          </StyledStatIcon>
          <StyledStatInfo>
            <StyledStatLabel>{t('analytics.savingsRate')}</StyledStatLabel>
            <StyledStatValue>{savingsRate.toFixed(1)}%</StyledStatValue>
          </StyledStatInfo>
        </StyledStatCard>
      </StyledStatsGrid>
      <StyledChartsGrid>
        <TrendChart data={data.monthlyTrends} title={t('analytics.monthlyTrends')} height={350} />

        <EnhancedPieChart data={data.categoryBreakdown} title={t('analytics.categoryBreakdown')} height={350} />
      </StyledChartsGrid>{' '}
      <EnhancedBarChart data={data.monthlyTrends} title={t('analytics.incomeVsExpenses')} height={300} />
      <StyledChartCard>
        <StyledChartTitle>{t('analytics.insights')}</StyledChartTitle>
        <StyledInsightsList>
          {data.insights.map((insight, index) => (
            <StyledInsightCard
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <StyledInsightText>{insight}</StyledInsightText>
            </StyledInsightCard>
          ))}
        </StyledInsightsList>
      </StyledChartCard>
    </StyledAnalyticsContainer>
  );
};

export default AnalyticsPage;
